import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import EventParticipantStatusItems from '../../TeamEvent/components/EventParticipantStatusItems'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { getSpecialization } from 'app/frontend/base/services/characterValues'
import { Loader2 } from 'lucide-react'

type Props = {
  main?: wowaudit.TeamMembership
  participant?: wowaudit.EventParticipantReference
  status: wowaudit.EventParticipantReference['status']
  canEditParticipant: boolean
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  isUpdatingStatus: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (nextStatus: wowaudit.EventParticipantReference['status']) => void
  triggerClassName: string
  nonEditableClassName?: string
  stopPropagation?: boolean
  iconSize?: 'small' | 'medium'
}

const EventParticipantStatusControl = ({
  main,
  participant,
  status,
  canEditParticipant,
  selectableStatuses,
  isUpdatingStatus,
  open,
  onOpenChange,
  onSelect,
  triggerClassName,
  nonEditableClassName = 'flex justify-center',
  stopPropagation = false,
  iconSize,
}: Props) => {
  const {
    data: { user },
  } = usePageInfo()
  const isOwnCharacter =
    !!main && !!user && user.id === main.characterReference.userId
  const participantDisplayCharacter =
    participant?.altCharacterReference ?? participant?.characterReference
  const participantDisplayMeta =
    participant && participantDisplayCharacter
      ? {
          id: participantDisplayCharacter.id,
          name: participantDisplayCharacter.name,
          characterClass: participantDisplayCharacter.characterClass,
          role: participant.role,
          specName:
            participant.specId == null
              ? undefined
              : getSpecialization(participant.specId.toString())?.name,
          specIcon:
            participant.specId == null
              ? undefined
              : getSpecialization(participant.specId.toString())?.icon,
        }
      : undefined
  const usesDifferentCharacter =
    !!participantDisplayMeta &&
    !!main &&
    participantDisplayMeta.id !== main.characterReference.id
  const hasSpecOverride =
    participant?.specId != null && participant.specId !== main?.specId
  const statusIcon = isUpdatingStatus ? (
    <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
  ) : (
    <EventStatusIcon status={status} size={iconSize ?? 'small'} />
  )
  const showParticipantIcon =
    !!participantDisplayMeta &&
    (usesDifferentCharacter || (isOwnCharacter && hasSpecOverride))
  const showSpecIcon =
    isOwnCharacter && hasSpecOverride && !usesDifferentCharacter
  const iconGroup =
    showParticipantIcon && participantDisplayMeta ? (
      <div className='flex items-center justify-center gap-1'>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex mr-1'>
              {showSpecIcon && participantDisplayMeta.specIcon ? (
                <BlizzardIcon
                  iconName={participantDisplayMeta.specIcon}
                  size='medium'
                />
              ) : (
                <CharacterClassIcon
                  size='medium'
                  characterClass={participantDisplayMeta.characterClass}
                />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className='flex items-center gap-1.5 justify-center'>
              <CharacterRoleIcon
                role={participantDisplayMeta.role}
                size='small'
              />
              <span>{participantDisplayMeta.name}</span>
            </div>
            <div className='text-xs text-muted-foreground text-center'>
              {participantDisplayMeta.specName
                ? `${participantDisplayMeta.specName} ${participantDisplayMeta.characterClass}`
                : participantDisplayMeta.characterClass}
            </div>
          </TooltipContent>
        </Tooltip>
        {statusIcon}
      </div>
    ) : (
      statusIcon
    )

  const stopEvent = (eventClick: { stopPropagation: () => void }) => {
    if (stopPropagation) {
      eventClick.stopPropagation()
    }
  }

  if (!canEditParticipant) {
    return (
      <div className={nonEditableClassName}>
        <span className={triggerClassName}>{iconGroup}</span>
      </div>
    )
  }

  return (
    <div className={nonEditableClassName} onClick={stopEvent}>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger
          className={triggerClassName}
          disabled={isUpdatingStatus}
          onClick={stopEvent}
        >
          {iconGroup}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-fit p-1'
          side='bottom'
          align='center'
          onClick={stopEvent}
        >
          <EventParticipantStatusItems
            currentStatus={status}
            statuses={selectableStatuses}
            onSelect={onSelect}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default EventParticipantStatusControl
